/** Questionnaire DOM shared by Person answers and the Race comparison. No server dependencies. */
import type { PublicQuestionnaireResponse } from "../api";
import { buildQuestionnaireStructure, type QuestionnaireSection } from "../../../lib/questionnaire-structure";
import { h } from "../dom";

/** Old cached payloads have only flat positions/text. Supply local IDs without inventing sections. */
export function publicQuestionnaireStructure(sections: QuestionnaireSection[] | undefined, questions: {
 id?: number; position:number; question:string; sectionId?:number|null; parentQuestionId?:number|null;
}[]) {
 const used = new Set(questions.flatMap(q=>q.id?[q.id]:[]));
 let fallback=1;
 const normalized=questions.map(q=>{
  while(used.has(fallback))fallback++;
  const id=q.id??fallback++; used.add(id);
  return {id,text:q.question,position:q.position,sectionId:q.sectionId??null,parentQuestionId:q.parentQuestionId??null};
 });
 return {...buildQuestionnaireStructure(sections??[],normalized),inputIds:normalized.map(q=>q.id)};
}
function answer(text:string|null|undefined,labelId:string,className="bsc-questionnaire__answer"):HTMLElement {
 return !text?.trim()?h("p",{class:"bsc-questionnaire__no-answer"},"No answer"):
  h("div",{class:className,"aria-labelledby":labelId},text.split(/\n{2,}/).map(para=>h("p",null,para)));
}
export function renderQuestionnaireAnswers(response:PublicQuestionnaireResponse,key:string):HTMLElement {
 const answers=response.answers??[],structure=publicQuestionnaireStructure(response.sections,answers);
 const byId=new Map(answers.map((a,i)=>[a.id??structure.inputIds[i],a.answerText]));
 const hierarchical=structure.sections.length>0||structure.flatQuestions.some(q=>q.parentQuestionId!==null);
 return h("div",{class:"bsc-questionnaire__body"},structure.groups.map(g=>{
  const sectionId=`${key}-section-${g.section?.id}`;
  return h("section",{"aria-labelledby":g.section?sectionId:null,class:g.section?"bsc-questionnaire__section":null},
   g.section?h("h3",{id:sectionId,class:"bsc-questionnaire__section-title"},g.section.title):null,
   g.questions.map(({question:q,subQuestions})=>{
    const promptId=`${key}-q${q.id}`;
    return h("div",{class:"bsc-questionnaire__question"},
     h(g.section?"h4":"h3",{class:"bsc-questionnaire__prompt",id:promptId},hierarchical?q.label+". "+q.text:q.text),
     answer(byId.get(q.id),promptId),
     subQuestions.length?h("dl",{class:"bsc-questionnaire__subquestions"},subQuestions.map(child=>{
      const childId=`${key}-q${child.id}`;
      return h("div",{class:"bsc-questionnaire__subquestion"},
       h("dt",{id:childId,class:"bsc-questionnaire__child-prompt"},child.label+". "+child.text),
       h("dd",null,answer(byId.get(child.id),childId,"bsc-questionnaire__child-answer")));
     })):null);
   }));
 }));
}
